import { Directive, DoCheck, ElementRef, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { ThemeService } from '../../../../services/theme.service';
import { ChartTokens, readChartTokens } from './chart-theme';

/**
 * Base for the report graphs: reads the bk-* colour tokens from the host (so
 * a `.dark` ancestor applies) and redraws when the app's theme changes.
 * Charts are rebuilt as new objects, never mutated in place, so ng2-charts
 * always sees the change.
 */
@Directive()
export abstract class ThemedChart implements DoCheck {
  protected host: ElementRef<HTMLElement> = inject(ElementRef);
  private theme = inject(ThemeService);
  protected isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  private appliedTheme: string | null = null;

  protected tokens: ChartTokens = readChartTokens(null);

  /** Rebuild the chart's data and options from the inputs and `tokens`. */
  protected abstract render(): void;

  /** Call from ngOnChanges: read the tokens now and redraw. */
  protected redraw(): void {
    if (this.isBrowser) this.tokens = readChartTokens(this.host.nativeElement);
    this.render();
  }

  ngDoCheck(): void {
    if (!this.isBrowser) return;
    const current = this.theme.currentTheme;
    if (current === this.appliedTheme) return;
    this.appliedTheme = current;
    // Let the wrapper's `.dark` class land, then redraw only if a colour changed
    // (this also corrects a first draw made before the host was in the page).
    setTimeout(() => {
      const next = readChartTokens(this.host.nativeElement);
      if (JSON.stringify(next) === JSON.stringify(this.tokens)) return;
      this.tokens = next;
      this.render();
    });
  }
}
